import type { BlockVersionMeta } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import { ZoneDimensionsField } from '../canvas/ZoneDimensionsField'

interface VersionHistoryPanelProps {
  blockId: string
  versions: BlockVersionMeta[]
  hasDraft: boolean
  onView: (version: number) => void
  onOpenDraft: () => void
  onFork: (version: number) => void
  onClose: () => void
}

export function VersionHistoryPanel({
  versions,
  hasDraft,
  onView,
  onOpenDraft,
  onFork,
  onClose,
}: VersionHistoryPanelProps) {
  const { t } = useTranslation()
  const sorted = [...versions].sort((a, b) => b.version - a.version)

  return (
    <aside
      data-testid="version-history-panel"
      className="flex w-72 shrink-0 flex-col overflow-auto border-l border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
    >
      <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3 dark:border-stone-800">
        <h3 className="text-sm font-semibold">{t('blockConfigurator.versionHistory.title')}</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-2 py-0.5 text-[11px] text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
        >
          {t('blockConfigurator.versionHistory.close')}
        </button>
      </div>
      <ul className="flex-1 space-y-1.5 p-3 text-xs">
        {hasDraft && (
          <li
            data-testid="version-row"
            className="rounded border border-amber-400 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-950"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">
                  {t('blockConfigurator.versionHistory.draft')}
                </span>
                <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-900">
                  {t('blockConfigurator.versionHistory.inProgress')}
                </span>
              </div>
              <button
                type="button"
                onClick={onOpenDraft}
                className="rounded bg-sky-600 px-2 py-0.5 text-[11px] text-white hover:bg-sky-700"
              >
                {t('blockConfigurator.versionHistory.openDraft')}
              </button>
            </div>
          </li>
        )}
        {sorted.map(v => (
          <li
            key={v.version}
            data-testid="version-row"
            className="rounded border border-stone-200 bg-stone-50 p-2 dark:border-stone-800 dark:bg-stone-950"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">v{v.version}</span>
                  <span className="text-[10px] text-stone-500">{v.createdAt?.slice(0, 10) ?? '—'}</span>
                  <ZoneDimensionsField widthMm={v.widthMm} heightMm={v.heightMm} />
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => onView(v.version)}
                  className="rounded border border-stone-300 px-2 py-0.5 text-[11px] text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300"
                >
                  {t('blockConfigurator.versionHistory.view')}
                </button>
                <button
                  type="button"
                  onClick={() => onFork(v.version)}
                  title={t('blockConfigurator.versionHistory.forkTooltip')}
                  // Deliberately muted/secondary vs. View's solid outline —
                  // Fork is a consequential action (creates a whole new
                  // block) and must not read as a peer of the plain "View"
                  // read-only action. No extra confirm here: ForkBlockModal
                  // already gates it.
                  className="rounded border border-dashed border-stone-400 px-2 py-0.5 text-[11px] text-stone-500 hover:bg-stone-100 dark:border-stone-600 dark:text-stone-400"
                >
                  {t('blockConfigurator.versionHistory.fork')}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </aside>
  )
}
